'use client';

import * as React from 'react';
import {
  ChatIcon,
  ChatToggle,
  DisconnectButton,
  GearIcon,
  LeaveIcon,
  MediaDeviceMenu,
  StartMediaButton,
  TrackToggle,
  useLocalParticipant,
  useLocalParticipantPermissions,
  useMaybeLayoutContext,
  usePersistentUserChoices,
  useTrackToggle,
} from '@livekit/components-react';
import { LocalAudioTrack, LocalVideoTrack, Track } from 'livekit-client';
import { ru } from '@/lib/ru';
import { getShortcutLabels } from '@/lib/shortcut-labels';
import { BackgroundPickerToggle } from './BackgroundPicker';
import { RaiseHandButton } from './RaiseHandButton';
import { AgimaBotIcon } from './AssistantPanel';
import { SCREEN_SHARE_CAPTURE_OPTIONS, trackSourceToProtocol } from '@/lib/shortcuts';

export type AgimaControlBarControls = {
  microphone?: boolean;
  camera?: boolean;
  chat?: boolean;
  screenShare?: boolean;
  raiseHand?: boolean;
  backgrounds?: boolean;
  leave?: boolean;
  settings?: boolean;
  assistant?: boolean;
};

function useMediaQuery(query: string): boolean {
  const getMatches = React.useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  }, [query]);

  const [matches, setMatches] = React.useState(getMatches);

  React.useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export function AgimaControlBar(props: {
  controls?: AgimaControlBarControls;
  saveUserChoices?: boolean;
  onDeviceError?: (error: { source: Track.Source; error: Error }) => void;
  assistantOpen?: boolean;
  onToggleAssistant?: () => void;
}) {
  const { controls, saveUserChoices = true, onDeviceError, assistantOpen = false, onToggleAssistant } = props;
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const layoutContext = useMaybeLayoutContext();
  const { microphoneTrack, cameraTrack } = useLocalParticipant();
  const audioTrack = microphoneTrack?.track as LocalAudioTrack | undefined;
  const videoTrack = cameraTrack?.track as LocalVideoTrack | undefined;
  const { enabled: isScreenShareEnabled } = useTrackToggle({
    source: Track.Source.ScreenShare,
    captureOptions: SCREEN_SHARE_CAPTURE_OPTIONS,
    onDeviceError: (error) => onDeviceError?.({ source: Track.Source.ScreenShare, error }),
  });

  React.useEffect(() => {
    if (layoutContext?.widget.state?.showChat !== undefined) {
      setIsChatOpen(layoutContext.widget.state.showChat);
    }
  }, [layoutContext?.widget.state?.showChat]);

  const isTooLittleSpace = useMediaQuery(`(max-width: ${isChatOpen ? 1000 : 760}px)`);
  const variation = isTooLittleSpace ? 'minimal' : 'verbose';
  const showIcon = true;
  const showText = variation === 'verbose';
  const shortcutLabels = getShortcutLabels();

  const visibleControls = { leave: true, ...controls };
  const localPermissions = useLocalParticipantPermissions();

  if (!localPermissions) {
    visibleControls.camera = false;
    visibleControls.chat = false;
    visibleControls.microphone = false;
    visibleControls.screenShare = false;
    visibleControls.raiseHand = false;
    visibleControls.backgrounds = false;
  } else {
    const canPublishSource = (source: Track.Source) =>
      localPermissions.canPublish &&
      (localPermissions.canPublishSources.length === 0 ||
        localPermissions.canPublishSources.includes(trackSourceToProtocol(source)));
    visibleControls.camera ??= canPublishSource(Track.Source.Camera);
    visibleControls.microphone ??= canPublishSource(Track.Source.Microphone);
    visibleControls.screenShare ??= canPublishSource(Track.Source.ScreenShare);
    visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
    visibleControls.raiseHand ??= true;
    visibleControls.backgrounds ??= visibleControls.camera !== false;
  }

  const browserSupportsScreenSharing =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  return (
    <div className="lk-control-bar">
      {visibleControls.microphone && (
        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Microphone}
            showIcon={showIcon}
            title={shortcutLabels.mic}
            onChange={(enabled, isUserInitiated) =>
              isUserInitiated ? saveAudioInputEnabled(enabled) : null
            }
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
          >
            {showText && ru.mic}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="audioinput"
              tracks={{ audioinput: audioTrack }}
              onActiveDeviceChange={(_kind, deviceId) =>
                saveAudioInputDeviceId(deviceId ?? 'default')
              }
            />
          </div>
        </div>
      )}
      {visibleControls.camera && (
        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Camera}
            showIcon={showIcon}
            title={shortcutLabels.camera}
            onChange={(enabled, isUserInitiated) =>
              isUserInitiated ? saveVideoInputEnabled(enabled) : null
            }
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Camera, error })}
          >
            {showText && ru.camera}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="videoinput"
              tracks={{ videoinput: videoTrack }}
              onActiveDeviceChange={(_kind, deviceId) =>
                saveVideoInputDeviceId(deviceId ?? 'default')
              }
            />
          </div>
        </div>
      )}
      {visibleControls.backgrounds && <BackgroundPickerToggle showText={showText} />}
      {visibleControls.screenShare && browserSupportsScreenSharing && (
        <TrackToggle
          source={Track.Source.ScreenShare}
          captureOptions={SCREEN_SHARE_CAPTURE_OPTIONS}
          showIcon={showIcon}
          title={shortcutLabels.screenShare}
          onDeviceError={(error) => onDeviceError?.({ source: Track.Source.ScreenShare, error })}
        >
          {showText && (isScreenShareEnabled ? ru.stopShare : ru.shareScreen)}
        </TrackToggle>
      )}
      {visibleControls.raiseHand && (
        <RaiseHandButton
          showIcon={showIcon}
          showText={showText}
          shortcutLabel={shortcutLabels.raiseHand}
        />
      )}
      {visibleControls.assistant && onToggleAssistant && (
        <button
          type="button"
          className="lk-button agima-assistant-toggle"
          aria-pressed={assistantOpen && !isChatOpen ? 'true' : 'false'}
          title={ru.assistantTitle}
          onClick={() => {
            // Chat and the assistant share one column: opening one closes the other.
            if (isChatOpen) layoutContext?.widget.dispatch?.({ msg: 'toggle_chat' });
            if (!assistantOpen || !isChatOpen) onToggleAssistant();
          }}
        >
          {showIcon && <AgimaBotIcon />}
          {showText && ru.assistant}
        </button>
      )}
      {visibleControls.chat && (
        <ChatToggle title={shortcutLabels.chat}>
          {showIcon && <ChatIcon />}
          {showText && ru.chat}
        </ChatToggle>
      )}
      {visibleControls.settings && (
        <button
          className="lk-button lk-settings-toggle"
          type="button"
          aria-pressed={layoutContext?.widget.state?.showSettings ? 'true' : 'false'}
          onClick={() => layoutContext?.widget.dispatch?.({ msg: 'toggle_settings' })}
        >
          {showIcon && <GearIcon />}
          {showText && ru.settings}
        </button>
      )}
      {visibleControls.leave && (
        <DisconnectButton title={shortcutLabels.leave}>
          {showIcon && <LeaveIcon />}
          {showText && ru.leave}
        </DisconnectButton>
      )}
      <StartMediaButton label={ru.startMedia} />
    </div>
  );
}
